import { ApolloProvider, useLazyQuery } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import ErrorBoundary from '@components/Error/ErrorBoundary'
import WithBrowserNotifications from '@components/Shared/HOC/WithBrowserNotifications'
import WithSharedData from '@components/Shared/HOC/WithSharedData'
import Toast from '@components/Toast'
import Noop from '@components/common/noop'
import { CounterType } from '@lib/graphql/__generated__/graphql'
import { useApollo } from '@lib/graphql/apollo'
import { GET_COUNTERS_WITH_THREADS } from '@lib/graphql/queries/Shared'
import { COUNT_SUBSCRIPTION } from '@lib/graphql/subscriptions/Shared'
import { unreadThreadsCountSubscriptionDataHandler } from '@lib/grid/subscriptions/subscriptionsEventHandlers'
import useLogout from '@lib/hooks/useLogout'
import useRouterSubscriptions from '@lib/hooks/useRouterSubscriptions'
import { GraphqlApi } from '@mono/packages/graphql/apollo/constants'
import DialogProvider from '@mono/packages/ui/components/Dialog/provider/DialogProvider'
import Head from 'next/head'
import React, { Suspense, useEffect, useMemo, useRef } from 'react'
import {
  DateOptionsProvider,
  TranslationsProviderWrapper,
  UiComponentsTranslationsProvider,
} from './Providers'
import RedirectHandler from './RedirectHandler'

interface IAppMainProps {
  Component: any
  pageProps: Record<string, any>
}

const CustomHead = () => {
  const { isAuthenticated } = useAuth0()

  const isSubscribed = useRef(false)

  const [getCounters, { data: counters, subscribeToMore }] = useLazyQuery(
    GET_COUNTERS_WITH_THREADS,
  )

  useEffect(() => {
    if (!isAuthenticated) return

    getCounters()

    if (!isSubscribed.current) {
      subscribeToMore({
        document: COUNT_SUBSCRIPTION,
        variables: {
          counterType: CounterType.UserUnreadThreads,
        },
        updateQuery: unreadThreadsCountSubscriptionDataHandler,
      })

      isSubscribed.current = true
    }
  }, [getCounters, subscribeToMore, isAuthenticated])

  const notificationCount = counters?.threadCounters?.unread ?? 0

  return (
    <Head>
      <link
        rel="icon"
        type="image/x-icon"
        href={
          notificationCount > 0
            ? '/favicon-red-dot.ico'
            : '/favicon-default.ico'
        }
      />
    </Head>
  )
}

const AppMain: React.FunctionComponent<IAppMainProps> = ({
  Component,
  pageProps,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const { logoutClient } = useLogout()

  const authConfig = useMemo(
    () => ({
      getHavelockAccessToken: getAccessTokenSilently,
      getProdactiveAccessToken: null,
      onLogout: () => {
        logoutClient({
          logoutParams: {
            returnTo: window.location.origin,
          },
        })
      },
    }),
    [getAccessTokenSilently, logoutClient],
  )

  const apolloClient = useApollo(
    GraphqlApi.Havelock,
    authConfig,
    pageProps?.initialApolloState,
  )

  const Layout = (Component as any).Layout || Noop

  useRouterSubscriptions()

  return (
    <Suspense fallback={null}>
      <ApolloProvider client={apolloClient}>
        <ErrorBoundary>
          <RedirectHandler>
            <TranslationsProviderWrapper>
              <DateOptionsProvider>
                <UiComponentsTranslationsProvider>
                  <DialogProvider>
                    <WithSharedData isAuthenticated={isAuthenticated}>
                      <WithBrowserNotifications>
                        <CustomHead />
                        <Layout pageProps={pageProps}>
                          <Component {...pageProps} />
                        </Layout>
                      </WithBrowserNotifications>
                    </WithSharedData>
                  </DialogProvider>
                  <Toast />
                </UiComponentsTranslationsProvider>
              </DateOptionsProvider>
            </TranslationsProviderWrapper>
          </RedirectHandler>
        </ErrorBoundary>
      </ApolloProvider>
    </Suspense>
  )
}

export default AppMain
